import React, { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import { changePasswordWithPasswordId, loginWithOtp, approveDeviceCode } from "services/auth.service";
import { setUserData, setJwtTokens } from "../../storage";
import { postAuthTokenToNative } from "utils/nativeBridge";
import { useLoginPage } from "hooks/useLoginPage";
import { useAppNavigate } from "hooks/useAppNavigate";
import { isValidPassword } from "utils/validators";
import PasswordInput from "components/PasswordInput";
import { useText } from "context/TextProvider";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsStatus, DeviceGrantStep } from "services/analyticsEvents";

function DeviceGrantForceChangePassword(): JSX.Element {
  const text = useText();
  const { changeId } = useParams<{ changeId: string }>();
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!newPassword) {
      setError(text.deviceGrant.changePassword.newPasswordRequired);
      return;
    }
    if (!isValidPassword(newPassword)) {
      setError(text.deviceGrant.changePassword.passwordRequirements);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(text.deviceGrant.changePassword.passwordsDoNotMatch);
      return;
    }
    setLoading(true);
    let oneTimePassword: string;
    try {
      ({ oneTimePassword } = await changePasswordWithPasswordId({
        passwordId: changeId ?? "",
        password: newPassword,
        url: loginPageConfig?.urls?.submit_forgot_password_otp,
      }));
    } catch {
      setError(text.deviceGrant.changePassword.resetFailed);
      setLoading(false);
      return;
    }
    try {
      const authData = await loginWithOtp({
        oneTimePassword,
        url: loginPageConfig?.urls?.otp_login,
      });
      setUserData(authData);
      const { user, ...tokens } = authData;
      setJwtTokens(tokens);
      const userCode = sessionStorage.getItem("device_grant_user_code") ?? undefined;
      await approveDeviceCode({
        user_code: userCode,
        access_token: authData.token,
        url: loginPageConfig?.urls?.approve_device_code,
      });
      trackEvent(AnalyticsEvent.APPROVE_DEVICE_CODE_SUCCEEDED, { step: DeviceGrantStep.APPROVE_DEVICE_CODE, status: AnalyticsStatus.SUCCEEDED });
      postAuthTokenToNative({
        token: authData.token,
        refreshToken: authData.refreshToken,
        tokenExpirationInstant: authData.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      appNavigate(`/device-grant-flow/complete`, {});
    } catch {
      setError(text.deviceGrant.changePassword.passwordChangedLoginFailed ?? text.deviceGrant.changePassword.resetFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="Device Grant Force Change Password" />
      <PageTitle>{text.deviceGrant.changePassword.title}</PageTitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <form onSubmit={handleChangePassword}>
        <PasswordInput
          id="newPassword"
          label={text.deviceGrant.changePassword.newPasswordLabel}
          value={newPassword}
          placeholder={text.deviceGrant.changePassword.newPasswordPlaceholder}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
        />
        {newPassword && !isValidPassword(newPassword) && <ErrorMessage>{text.deviceGrant.changePassword.passwordRequirements}</ErrorMessage>}
        <PasswordInput
          id="confirmPassword"
          label={text.deviceGrant.changePassword.confirmPasswordLabel}
          value={confirmPassword}
          placeholder={text.deviceGrant.changePassword.confirmPasswordPlaceholder}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />

        <LoadingButton
          loading={loading}
          type="submit"
          disabled={loading}
          sx={{ width: "100%", height: "3.5rem", mb: "1rem" }}
        >
          {text.deviceGrant.changePassword.updatePasswordButton}
        </LoadingButton>
      </form>

      <Box sx={{ textAlign: "center", fontSize: "0.875rem", color: "text.secondary", mb: "1.5rem" }}>
        <MuiLink color="secondary" component={RouterLink} to={`/device-grant-flow/login?config_id=${configId}`} replace sx={{ fontWeight: 500 }}>
          {text.deviceGrant.changePassword.backToLoginLink}
        </MuiLink>
      </Box>
    </>
  );
}

export default DeviceGrantForceChangePassword;
